'use client';

import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardAction,
} from '@packages/shared/src/ui/Card/Card';
import { useIntl, defineMessages } from 'react-intl';
import type { CustomerStatus } from '@packages/gql/generated/shopping/graphql';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { Link } from '@packages/modules/src/Link/Link';
import { AccountItemCard } from './AccountItemCard';
import { OrderDetailsButton } from '../CustomerOrders/OrderDetailsButton.client';

const messages = defineMessages({
  cardHeadline: {
    id: 'OrdersCard.headline',
    defaultMessage: 'Meine Bestellungen',
  },
  emptyMessage: {
    id: 'OrdersCard.empty.message',
    defaultMessage: 'Aktuell hat sich kein Status deiner Bestellungen geändert.',
  },
  orderStatus: {
    id: 'OrdersCard.Status',
    defaultMessage: 'Status: {status}',
  },
  viewAllOrders: {
    id: 'OrdersCard.viewAll',
    defaultMessage: 'Alle Bestellungen und Lieferungen',
  },
  hintText: {
    id: 'OrdersCard.hintText',
    defaultMessage: 'Bei folgenden Bestellungen hat sich der Status geändert:',
  },
});

export type OrdersCardProps = {
  /** List of recent returns to display */
  shipments?: CustomerStatus['shipments'];
};

/**
 * OrdersCard component displays recent returns information
 * on the my account dashboard
 */
export const OrdersCard = ({ shipments = [] }: OrdersCardProps) => {
  const {
    order: {
      paths: { myAccountOrders },
    },
  } = useConfig();
  const { formatMessage, locale } = useIntl();

  const hasShipments = shipments && shipments.length > 0;

  // Construct URLs for my transactions - directly use myAccountBookings path
  const visibleShipments = hasShipments ? shipments.slice(0, 3) : [];
  return (
    <Card hasAutoSeparation>
      <CardHeader hasDivider>
        <h2 className="text-size-headline-base">{formatMessage(messages.cardHeadline)}</h2>
      </CardHeader>

      <CardContent className="flex flex-col gap-1.5 py-1.5">
        {!hasShipments ? (
          <p className="text-color-text-dark text-size-body-xs">
            {formatMessage(messages.emptyMessage)}
          </p>
        ) : (
          <>
            <p className="text-size-body-xs">{formatMessage(messages.hintText)}</p>
            {visibleShipments.map((shipmentItem) => {
              // Show max 3 items, rest go to additional count
              const visibleItems = shipmentItem.items.slice(0, 3);
              const additionalCount =
                (shipmentItem.additionalItemCount || 0) +
                Math.max(0, shipmentItem.items.length - 4);
              return (
                <AccountItemCard
                  key={`${shipmentItem.orderNo}-${shipmentItem.date}`}
                  items={visibleItems}
                  additionalCount={additionalCount}
                  orderNo={shipmentItem.orderNo}
                  className="flex flex-col items-start gap-1 px-1.5 py-1.5"
                >
                  <div className="text-size-body-xs flex">
                    {formatMessage(messages.orderStatus, {
                      status: (
                        <p className="text-color-success-dark ml-0.5 font-bold">
                          {shipmentItem.status}
                        </p>
                      ),
                    })}
                  </div>
                  {shipmentItem.orderNo && <OrderDetailsButton orderNo={shipmentItem.orderNo} />}
                </AccountItemCard>
              );
            })}
          </>
        )}
      </CardContent>
      {/* Action link section */}
      <CardFooter className="flex items-center gap-2 px-2 py-1">
        <CardAction icon="icon-emp-tracking">
          <Link
            href={getLocalizedPath(myAccountOrders, locale)}
            className="text-color-text-dark text-size-body-sm flex items-center hover:underline"
          >
            {formatMessage(messages.viewAllOrders)}
          </Link>
        </CardAction>
      </CardFooter>
    </Card>
  );
};
